/* ============================================================================
   FaithCore OS — theme.js
   Core interactive behaviour. Loaded with `defer`. No dependencies.

   Architecture: everything is a Custom Element. That gives us free lifecycle
   hooks that work with Shopify's Theme Editor section reloads — when a
   section re-renders, its elements are constructed again automatically and
   we never leak listeners.

   Contents:
     1. Utilities (fetch helpers, focus trap, money)
     2. <fc-drawer> + openers
     3. Header scroll state
     4. Announcement rotator
     5. Scroll reveal
     6. Cart (add / change / drawer refresh)
     7. <fc-product-form> + variant picker
     8. <fc-gallery>
     9. Sticky mobile buy bar
    10. <fc-facets> (filter/sort + infinite scroll)
    11. <fc-predictive-search>
    12. Recently viewed
   ========================================================================= */
(() => {
  'use strict';

  /* =========================================================================
     1. UTILITIES
     ====================================================================== */
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

  // Shopify's routes are localised (e.g. /fr/cart). window.routes is printed
  // in theme.liquid so JS never hardcodes a path.
  const routes = window.FaithCore?.routes || {};

  const fetchJSON = async (url, options = {}) => {
    const res = await fetch(url, {
      ...options,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(options.headers || {}) }
    });
    const data = await res.json();
    if (!res.ok) throw Object.assign(new Error(data.description || data.message || 'Request failed'), { data });
    return data;
  };

  const fetchSection = async (url) => {
    const res = await fetch(url);
    const text = await res.text();
    return new DOMParser().parseFromString(text, 'text/html');
  };

  const debounce = (fn, wait = 300) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
  };

  const formatMoney = (cents) => {
    const format = window.FaithCore?.moneyFormat || '${{amount}}';
    const amount = (cents / 100).toLocaleString(document.documentElement.lang || 'en', {
      minimumFractionDigits: 2, maximumFractionDigits: 2
    });
    return format.replace(/\{\{\s*amount[^}]*\}\}/, amount);
  };

  // Announce to screen readers without stealing focus.
  const live = (msg) => {
    const region = $('#fc-live-region');
    if (region) region.textContent = msg;
  };

  /* =========================================================================
     2. DRAWER
     Used by cart, search, and the mobile menu. Handles overlay, focus trap,
     scroll lock, Escape, and focus restoration.
     ====================================================================== */
  class FCDrawer extends HTMLElement {
    connectedCallback() {
      this.overlay = $('[data-fc-overlay]');
      this.opener = null;
      this.onKeydown = this.onKeydown.bind(this);
      $$('[data-fc-close]', this).forEach((b) => b.addEventListener('click', () => this.close()));
      this.overlay?.addEventListener('click', () => { if (this.classList.contains('is-open')) this.close(); });
    }

    open(opener) {
      this.opener = opener || document.activeElement;
      // Close any sibling drawer first — only one at a time.
      $$('fc-drawer.is-open').forEach((d) => d !== this && d.close());
      this.classList.add('is-open');
      this.overlay?.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('fc-no-scroll');
      document.addEventListener('keydown', this.onKeydown);
      // Wait for the transition to start before focusing, otherwise Safari
      // scrolls the still-offscreen drawer into view.
      requestAnimationFrame(() => {
        const first = $('[data-fc-autofocus]', this) || $(FOCUSABLE, this);
        first?.focus();
      });
      this.dispatchEvent(new CustomEvent('fc:drawer:open', { bubbles: true }));
    }

    close() {
      this.classList.remove('is-open');
      this.overlay?.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('fc-no-scroll');
      document.removeEventListener('keydown', this.onKeydown);
      this.opener?.focus();
      this.dispatchEvent(new CustomEvent('fc:drawer:close', { bubbles: true }));
    }

    onKeydown(e) {
      if (e.key === 'Escape') { e.preventDefault(); this.close(); return; }
      if (e.key !== 'Tab') return;
      const items = $$(FOCUSABLE, this).filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }
  customElements.define('fc-drawer', FCDrawer);

  // Any [data-fc-open="drawer-id"] opens the matching drawer.
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-fc-open]');
    if (!trigger) return;
    const drawer = document.getElementById(trigger.dataset.fcOpen);
    if (!drawer) return;
    e.preventDefault();
    drawer.open(trigger);
  });

  /* =========================================================================
     3. HEADER SCROLL STATE
     A zero-height sentinel above the header. When it leaves the viewport we
     know we've scrolled — no scroll listener, no jank.
     ====================================================================== */
  function initHeader() {
    const header = $('[data-fc-header]');
    if (!header) return;
    let sentinel = $('[data-fc-header-sentinel]');
    if (!sentinel) {
      sentinel = document.createElement('div');
      sentinel.setAttribute('data-fc-header-sentinel', '');
      sentinel.style.cssText = 'position:absolute;top:0;height:1px;width:1px;pointer-events:none;';
      document.body.prepend(sentinel);
    }
    new IntersectionObserver(([entry]) => {
      header.classList.toggle('is-scrolled', !entry.isIntersecting);
    }).observe(sentinel);

    // Keyboard support for the mega menu (hover is CSS-only).
    $$('[data-fc-mega-parent]', header).forEach((item) => {
      const link = $('.fc-nav__link', item);
      link?.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
          e.preventDefault();
          const open = item.dataset.open === 'true';
          item.dataset.open = String(!open);
          link.setAttribute('aria-expanded', String(!open));
          if (!open) $('.fc-mega__link', item)?.focus();
        }
      });
      item.addEventListener('focusout', (e) => {
        if (!item.contains(e.relatedTarget)) { item.dataset.open = 'false'; link?.setAttribute('aria-expanded', 'false'); }
      });
      item.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') { item.dataset.open = 'false'; link?.setAttribute('aria-expanded', 'false'); link?.focus(); }
      });
    });
  }

  /* =========================================================================
     4. ANNOUNCEMENT ROTATOR
     ====================================================================== */
  class FCAnnounce extends HTMLElement {
    connectedCallback() {
      this.messages = $$('[data-fc-msg]', this);
      if (this.messages.length < 2) return;
      this.index = 0;
      this.interval = (parseInt(this.dataset.interval, 10) || 5) * 1000;
      this.start();
      // Pause on hover/focus so a link is never yanked out from under a click.
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
      this.addEventListener('focusin', () => this.stop());
      this.addEventListener('focusout', () => this.start());
    }
    disconnectedCallback() { this.stop(); }
    start() { this.stop(); this.timer = setInterval(() => this.next(), this.interval); }
    stop() { clearInterval(this.timer); }
    next() {
      this.messages[this.index].classList.remove('is-active');
      this.index = (this.index + 1) % this.messages.length;
      this.messages[this.index].classList.add('is-active');
    }
  }
  customElements.define('fc-announce', FCAnnounce);

  /* =========================================================================
     5. SCROLL REVEAL
     ====================================================================== */
  function initReveal() {
    const nodes = $$('[data-fc-reveal]:not(.is-revealed)');
    if (!nodes.length || !('IntersectionObserver' in window)) {
      nodes.forEach((n) => n.classList.add('is-revealed'));
      return;
    }
    const io = new IntersectionObserver((entries, obs) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        obs.unobserve(entry.target); // reveal once, then stop watching
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.06 });
    nodes.forEach((n) => io.observe(n));
  }
  // Add the gate class as early as possible so there's no flash of positioned
  // content, but only when JS is actually alive to remove it again.
  document.documentElement.classList.add('fc-reveal-on');

  /* Server-rendered form feedback (contact, login, addresses, password) is
     marked [data-fc-autofocus]. After a POST + redirect the browser restores
     scroll but says nothing, so a screen reader user lands on a page that has
     silently changed. Moving focus to the message is what announces it.
     Drawers handle their own focus, so skip anything inside one. */
  const feedback = $('[data-fc-autofocus]:not(.fc-drawer [data-fc-autofocus])');
  if (feedback) {
    feedback.focus({ preventScroll: true });
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    feedback.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
  }

  /* =========================================================================
     6. CART
     ====================================================================== */
  const Cart = {
    // Which sections Shopify should re-render alongside the mutation. Read from
    // the DOM rather than hard-coded, so the cart *page* ([data-fc-section=
    // "main-cart"]) updates when the drawer is switched off, and neither one
    // asks the server to render markup that isn't on screen.
    sectionList() {
      const ids = [...document.querySelectorAll('[data-fc-section]')]
        .map((el) => el.dataset.fcSection)
        .filter(Boolean);
      return [...new Set(ids)].join(',');
    },

    async add(formData) {
      const body = new FormData(formData instanceof HTMLFormElement ? formData : undefined);
      if (!(formData instanceof HTMLFormElement)) {
        Object.entries(formData).forEach(([k, v]) => body.append(k, v));
      }
      // Ask Shopify to return the re-rendered cart drawer with the response,
      // so one round trip covers both the mutation and the UI update.
      body.append('sections', this.sectionList());
      body.append('sections_url', window.location.pathname);

      const res = await fetch(`${routes.cart_add_url}.js`, { method: 'POST', body });
      const data = await res.json();
      if (!res.ok) throw Object.assign(new Error(data.description || data.message), { data });
      this.renderSections(data.sections);
      document.dispatchEvent(new CustomEvent('fc:cart:updated', { detail: data }));
      live(window.FaithCore?.strings?.addedToCart || 'Added to cart');
      $('#fc-cart-drawer')?.open();
      return data;
    },

    async change({ line, quantity }) {
      const data = await fetchJSON(`${routes.cart_change_url}.js`, {
        method: 'POST',
        body: JSON.stringify({ line, quantity, sections: this.sectionList(), sections_url: window.location.pathname })
      });
      this.renderSections(data.sections);
      document.dispatchEvent(new CustomEvent('fc:cart:updated', { detail: data }));
      return data;
    },

    // Swap in server-rendered HTML for the parts of the page that changed.
    renderSections(sections = {}) {
      Object.entries(sections).forEach(([id, html]) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const incoming = doc.querySelector(`[data-fc-section="${id}"]`);
        const current = document.querySelector(`[data-fc-section="${id}"]`);
        if (incoming && current) current.innerHTML = incoming.innerHTML;
      });
    }
  };
  window.FaithCore = Object.assign(window.FaithCore || {}, { Cart, formatMoney });

  // Quantity steppers + line removal, delegated so re-rendered cart HTML works.
  document.addEventListener('click', async (e) => {
    const step = e.target.closest('[data-fc-qty-step]');
    if (step) {
      const input = $('input', step.parentElement);
      const delta = step.dataset.fcQtyStep === 'up' ? 1 : -1;
      const next = Math.max(parseInt(input.min || 0, 10), (parseInt(input.value, 10) || 0) + delta);
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
    const remove = e.target.closest('[data-fc-cart-remove]');
    if (remove) {
      e.preventDefault();
      remove.closest('.fc-cart-item')?.style.setProperty('opacity', '0.4');
      await Cart.change({ line: remove.dataset.fcCartRemove, quantity: 0 });
    }
  });

  document.addEventListener('change', async (e) => {
    const input = e.target.closest('[data-fc-cart-qty]');
    if (!input) return;
    await Cart.change({ line: input.dataset.fcCartQty, quantity: parseInt(input.value, 10) });
  });

  /* =========================================================================
     7. PRODUCT FORM + VARIANT PICKER
     ====================================================================== */
  class FCProductForm extends HTMLElement {
    connectedCallback() {
      this.form = $('form', this);
      this.button = $('[data-fc-add]', this);
      this.errorEl = $('[data-fc-form-error]', this);
      this.form?.addEventListener('submit', this.onSubmit.bind(this));
    }
    async onSubmit(e) {
      // Cart drawer enabled → intercept. Disabled → let the browser POST to /cart.
      if (this.dataset.drawer !== 'true') return;
      e.preventDefault();
      if (this.button.getAttribute('aria-disabled') === 'true') return;
      this.setLoading(true);
      this.errorEl && (this.errorEl.textContent = '');
      try {
        await Cart.add(this.form);
      } catch (err) {
        if (this.errorEl) this.errorEl.textContent = err.data?.description || err.message;
        live(err.message);
      } finally {
        this.setLoading(false);
      }
    }
    setLoading(state) {
      this.button?.classList.toggle('is-loading', state);
      this.button?.setAttribute('aria-busy', String(state));
    }
  }
  customElements.define('fc-product-form', FCProductForm);

  class FCVariantPicker extends HTMLElement {
    connectedCallback() {
      this.variants = JSON.parse($('[data-fc-variants]', this)?.textContent || '[]');
      this.addEventListener('change', this.onChange.bind(this));
    }
    get selectedOptions() {
      return $$('input:checked, select', this).map((el) => el.value);
    }
    onChange() {
      const selected = this.selectedOptions;
      const variant = this.variants.find((v) => v.options.every((opt, i) => opt === selected[i]));
      this.updateAvailability(selected);
      if (!variant) { this.setUnavailable(); return; }
      this.updateURL(variant);
      this.updateInput(variant);
      this.updatePrice(variant);
      this.updateButton(variant);
      this.updateMedia(variant);
      document.dispatchEvent(new CustomEvent('fc:variant:change', { detail: { variant } }));
    }
    // Grey out combinations that don't exist, one option level at a time.
    updateAvailability(selected) {
      const groups = $$('[data-fc-option-index]', this);
      groups.forEach((group) => {
        const index = parseInt(group.dataset.fcOptionIndex, 10);
        $$('input', group).forEach((input) => {
          const trial = [...selected];
          trial[index] = input.value;
          const match = this.variants.find((v) =>
            v.options.every((opt, i) => i > index ? true : opt === trial[i]));
          const exists = this.variants.some((v) => v.options[index] === input.value && v.available &&
            v.options.every((opt, i) => i === index || opt === selected[i]));
          input.disabled = !match;
          input.closest('.fc-swatch')?.classList.toggle('is-unavailable', !exists);
        });
      });
    }
    updateURL(variant) {
      if (this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${variant.id}`);
    }
    updateInput(variant) {
      const input = $('input[name="id"]', this.closest('fc-product-form') || document);
      if (input) { input.value = variant.id; input.dispatchEvent(new Event('change', { bubbles: true })); }
    }
    updatePrice(variant) {
      const target = $('[data-fc-price-target]');
      if (!target) return;
      const onSale = variant.compare_at_price > variant.price;
      target.innerHTML = `
        <span class="fc-price__regular">${formatMoney(variant.price)}</span>
        ${onSale ? `<s class="fc-price__compare">${formatMoney(variant.compare_at_price)}</s>` : ''}`;
      target.classList.toggle('fc-price--on-sale', onSale);
      $('[data-fc-sticky-price]')?.replaceChildren(document.createTextNode(formatMoney(variant.price)));
    }
    updateButton(variant) {
      const strings = window.FaithCore?.strings || {};
      $$('[data-fc-add]').forEach((btn) => {
        const label = $('[data-fc-add-label]', btn) || btn;
        btn.setAttribute('aria-disabled', String(!variant.available));
        btn.disabled = !variant.available;
        label.textContent = variant.available ? (strings.addToCart || 'Equip Your Journey') : (strings.soldOut || 'Awaiting Restock');
      });
    }
    updateMedia(variant) {
      if (!variant.featured_media) return;
      $('fc-gallery')?.selectByMediaId(variant.featured_media.id);
    }
    setUnavailable() {
      const strings = window.FaithCore?.strings || {};
      $$('[data-fc-add]').forEach((btn) => {
        btn.setAttribute('aria-disabled', 'true');
        btn.disabled = true;
        ($('[data-fc-add-label]', btn) || btn).textContent = strings.unavailable || 'Unavailable';
      });
    }
  }
  customElements.define('fc-variant-picker', FCVariantPicker);

  /* =========================================================================
     8. GALLERY
     ====================================================================== */
  class FCGallery extends HTMLElement {
    connectedCallback() {
      this.slides = $$('[data-fc-media-id]', this);
      $$('[data-fc-thumb]', this).forEach((thumb) => {
        thumb.addEventListener('click', () => this.selectByMediaId(thumb.dataset.fcThumb));
      });
      this.addEventListener('keydown', (e) => {
        if (!['ArrowLeft', 'ArrowRight'].includes(e.key)) return;
        const active = this.slides.findIndex((s) => s.classList.contains('is-active'));
        const next = (active + (e.key === 'ArrowRight' ? 1 : -1) + this.slides.length) % this.slides.length;
        this.selectByMediaId(this.slides[next].dataset.fcMediaId);
      });
    }
    selectByMediaId(id) {
      const target = String(id);
      this.slides.forEach((slide) => slide.classList.toggle('is-active', slide.dataset.fcMediaId === target));
      $$('[data-fc-thumb]', this).forEach((thumb) => {
        const on = thumb.dataset.fcThumb === target;
        thumb.setAttribute('aria-current', String(on));
        if (on) thumb.scrollIntoView({ inline: 'nearest', block: 'nearest', behavior: 'smooth' });
      });
    }
  }
  customElements.define('fc-gallery', FCGallery);

  /* =========================================================================
     9. STICKY MOBILE BUY BAR
     Appears only once the real Add to Cart button has scrolled away.
     ====================================================================== */
  function initStickyBuy() {
    const bar = $('[data-fc-sticky-buy]');
    const anchor = $('[data-fc-buy-anchor]');
    if (!bar || !anchor) return;
    new IntersectionObserver(([entry]) => {
      bar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
    }, { threshold: 0 }).observe(anchor);
  }

  /* =========================================================================
     10. FACETS — filter, sort, infinite scroll
     ====================================================================== */
  class FCFacets extends HTMLElement {
    connectedCallback() {
      this.results = $('[data-fc-results]');
      this.form = $('form', this);
      this.addEventListener('change', debounce(() => this.apply(), 250));
      document.addEventListener('click', (e) => {
        const chip = e.target.closest('[data-fc-remove-filter]');
        if (chip) { e.preventDefault(); this.navigate(chip.href); }
      });
      window.addEventListener('popstate', () => this.navigate(window.location.href, false));
      if (this.dataset.infinite === 'true') this.initInfinite();
    }
    apply() {
      const params = new URLSearchParams(new FormData(this.form));
      // Drop empty values so URLs stay shareable and clean.
      Array.from(params.entries()).forEach(([k, v]) => { if (!v) params.delete(k); });
      this.navigate(`${window.location.pathname}?${params}`);
    }
    async navigate(url, push = true) {
      this.results?.setAttribute('aria-busy', 'true');
      const doc = await fetchSection(url);
      const incoming = doc.querySelector('[data-fc-results]');
      const facets = doc.querySelector('[data-fc-facets-inner]');
      if (incoming && this.results) this.results.innerHTML = incoming.innerHTML;
      if (facets) $('[data-fc-facets-inner]', this).innerHTML = facets.innerHTML;
      this.results?.setAttribute('aria-busy', 'false');
      if (push) window.history.pushState({}, '', url);
      initReveal();
      window.FaithCoreEffects?.init();
      live(doc.querySelector('[data-fc-count]')?.textContent || '');
    }
    // Infinite scroll: watch a sentinel after the grid, append the next page.
    initInfinite() {
      const load = async () => {
        const next = $('[data-fc-next]');
        if (!next || this.loading) return;
        this.loading = true;
        const doc = await fetchSection(next.href);
        const grid = $('[data-fc-grid]', this.results);
        doc.querySelectorAll('[data-fc-grid] > *').forEach((n) => grid.appendChild(n));
        const newNext = doc.querySelector('[data-fc-next]');
        next.replaceWith(newNext || '');
        this.loading = false;
        initReveal();
      };
      const io = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) load();
      }, { rootMargin: '600px' });
      const sentinel = $('[data-fc-infinite-sentinel]');
      if (sentinel) io.observe(sentinel);
    }
  }
  customElements.define('fc-facets', FCFacets);

  /* =========================================================================
     11. PREDICTIVE SEARCH
     ====================================================================== */
  class FCPredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = $('input[type="search"]', this);
      this.panel = $('[data-fc-predictive-results]', this);
      this.input?.addEventListener('input', debounce((e) => this.search(e.target.value), 280));
    }
    async search(q) {
      if (q.length < 2) { this.panel.innerHTML = ''; return; }
      const url = `${routes.predictive_search_url}?q=${encodeURIComponent(q)}&resources[type]=product,article,collection&resources[limit]=6&section_id=predictive-search`;
      try {
        const doc = await fetchSection(url);
        const html = doc.querySelector('[data-fc-predictive-inner]');
        this.panel.innerHTML = html ? html.innerHTML : '';
      } catch { /* search failing should never break the page */ }
    }
  }
  customElements.define('fc-predictive-search', FCPredictiveSearch);

  /* =========================================================================
     12. RECENTLY VIEWED
     Stores IDs only (no PII) in localStorage; the section fetches real
     product HTML from the Search API so prices/stock are never stale.
     ====================================================================== */
  const Recent = {
    key: 'faithcore:recently-viewed',
    read() {
      try { return JSON.parse(localStorage.getItem(this.key) || '[]'); } catch { return []; }
    },
    push(id) {
      if (!id) return;
      const list = this.read().filter((x) => x !== id);
      list.unshift(id);
      try { localStorage.setItem(this.key, JSON.stringify(list.slice(0, 12))); } catch { /* private mode */ }
    }
  };

  class FCRecentlyViewed extends HTMLElement {
    async connectedCallback() {
      const exclude = this.dataset.currentId;
      const ids = Recent.read().filter((id) => String(id) !== String(exclude)).slice(0, parseInt(this.dataset.limit, 10) || 4);
      if (!ids.length) { this.hidden = true; return; }
      // Shopify's search endpoint accepts an OR'd id query.
      const query = ids.map((id) => `id:${id}`).join(' OR ');
      const url = `${routes.search_url}?q=${encodeURIComponent(query)}&type=product&section_id=${this.dataset.sectionId}`;
      try {
        const doc = await fetchSection(url);
        const inner = doc.querySelector('[data-fc-recent-inner]');
        if (!inner || !inner.children.length) { this.hidden = true; return; }
        $('[data-fc-recent-target]', this).innerHTML = inner.innerHTML;
        this.hidden = false;
        initReveal();
      } catch { this.hidden = true; }
    }
  }
  customElements.define('fc-recently-viewed', FCRecentlyViewed);

  if (window.FaithCore?.productId) Recent.push(window.FaithCore.productId);

  /* =========================================================================
     BOOT
     ====================================================================== */
  const boot = () => {
    initHeader();
    initReveal();
    initStickyBuy();
    document.body.classList.add('fc-loaded');
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // Theme Editor hooks — keep the preview fully interactive while editing.
  document.addEventListener('shopify:section:load', () => { initHeader(); initReveal(); initStickyBuy(); });
  document.addEventListener('shopify:section:select', (e) => {
    const drawer = e.target.querySelector('fc-drawer');
    drawer?.open();
  });
})();
